import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import { MaintenanceBannerState, MaintenanceContext } from 'src/services/maintenanceState';
import {
  formatMaintenanceDay,
  formatMaintenanceTime,
} from 'src/components/layouts/MaintenanceBanner/maintenanceFormat';
import styles from 'src/components/layouts/MaintenanceBanner/MaintenanceBanner.module.scss';

export interface MaintenanceBannerViewProps {
  state: MaintenanceBannerState;
  context: MaintenanceContext;
  onDismiss: () => void;
}

type Tone = 'info' | 'warning' | 'success';

interface BannerContent {
  tone: Tone;
  icon: IconName;
  title: string;
  copy: string;
  dismissible: boolean;
}

/**
 * Pure rendering of a banner state. Which state is shown is the hook's job
 * (`useMaintenanceBanner`); this component only maps a state to tone, icon and
 * copy. A message from the settings replaces the default copy of "planned" and
 * "running", never a title.
 */
const MaintenanceBannerView: React.FC<MaintenanceBannerViewProps> = ({ state, context, onDismiss }) => {
  const { t } = useTranslation();

  if (state.kind === 'hidden') return null;

  const content = ((): BannerContent => {
    switch (state.kind) {
      case 'planned': {
        const { start, end, message } = state.window;
        return {
          tone: 'info',
          icon: 'infoCircle',
          dismissible: true,
          title: t('maintenance.planned_title', {
            day: formatMaintenanceDay(start, Date.now(), t),
            from: formatMaintenanceTime(start),
            to: formatMaintenanceTime(end),
          }),
          copy: message ?? t('maintenance.planned_copy'),
        };
      }
      case 'running': {
        const { end, message } = state.window;
        return {
          tone: 'warning',
          icon: 'warning',
          dismissible: false,
          title: state.overdue
            ? t('maintenance.running_overdue_title')
            : t('maintenance.running_title', { to: formatMaintenanceTime(end) }),
          copy:
            message ?? t(context === 'login' ? 'maintenance.running_copy_login' : 'maintenance.running_copy_portal'),
        };
      }
      case 'done':
        return {
          tone: 'success',
          icon: 'checkCircle',
          dismissible: true,
          title: t('maintenance.done_title'),
          copy: t('maintenance.done_copy'),
        };
      default:
        return {
          tone: 'warning',
          icon: 'warning',
          dismissible: false,
          title: t('maintenance.unreachable_title'),
          copy: t('maintenance.unreachable_copy'),
        };
    }
  })();

  return (
    <div
      role="status"
      data-testid="maintenance-banner"
      data-state={state.kind}
      className={classNames(styles.root, styles[content.tone])}>
      <div className={styles.text}>
        <span className={styles.icon} aria-hidden="true">
          <Icon name={content.icon} width={16} height={16} />
        </span>
        <strong className="typo-text-14--bold">{content.title}</strong>
        <span className="typo-text-14">{content.copy}</span>
      </div>
      {content.dismissible && (
        <button type="button" className={styles.dismiss} aria-label={t('maintenance.dismiss')} onClick={onDismiss}>
          <Icon name="close" width={18} height={18} />
        </button>
      )}
    </div>
  );
};

export default MaintenanceBannerView;
